import React from 'react'
import { Navigate, Outlet, useParams } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Plus, Upload } from '@factorialco/f0-react/icons/app'
import Envelope from '@factorialco/f0-react/icons/app/Envelope'
import {
  CompensationLegalEntitiesDocument,
  EmployeeUpdatesDataCollectionDocument,
  EmployeeUpdatesDataCollectionQuery,
  EmployeeUpdatesStatusCountsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import type { DefaultParams } from 'modules/bookkeepers/hooks/useIncidenceFilters'
import { IncidenceFilterProvider } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import { MessageFilterProvider } from 'modules/bookkeepers/lib/MessageFilterContext'
import { FACTORIAL_APP } from 'modules/bookkeepers/lib/amplitudeData'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import { translatedIncidenceName } from 'modules/bookkeepers/lib/dynamicTranslations'
import { SORT_ATTRIBUTES } from 'modules/bookkeepers/lib/tableSortColumns'
import { getStatusConfig } from 'modules/bookkeepers/lib/tagColor'
import { getUpdateTypeFilterOptions } from 'modules/compensations/lib/updateTypeFilterOptions'
import { useWorkplaceFilter } from 'modules/employees/lib/filters/workplace'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import {
  INCIDENCE_STATUSES,
  INHOUSE_INCIDENCE_STATUSES,
  inhouseStatusCopies,
  professionalsStatusCopies,
} from 'modules/payroll_bundle/lib/Incidences/Status'
import NewManualIncidenceModal from 'modules/payroll_bundle/pages/IncidencesPage/components/_ManualIncidenceModal/_New'
import PayrollExportModal from 'modules/payroll_exporter/components/PayrollExportModal'

import EmployeeUpdatesDataCollectionContent from './EmployeeUpdatesDataCollectionContent'
import {
  DataCollectionState,
  FilterState,
  createDynamicPresets,
  createStateChangeHandler,
  getStatusCount,
} from './utils'

type IncidenceItem = NonNullable<
  EmployeeUpdatesDataCollectionQuery['bookkeepersManagement']
>['incidencesConnection']['nodes'][0]

// Message source constants to avoid hardcoded strings
const MESSAGE_FROM = {
  ADMIN: 'admin',
  BOOKKEEPER: 'bookkeeper',
} as const

const DEFAULT_PAGE = 1
const DEFAULT_PER_PAGE = 1000

const hasUnreadMessage = (incidence: IncidenceItem, isBookkeeperView: boolean) =>
  incidence.hasMessage &&
  incidence.messageFrom === (isBookkeeperView ? MESSAGE_FROM.ADMIN : MESSAGE_FROM.BOOKKEEPER)

const getMessageFilterVariables = (
  hasMessageValues: (boolean | string)[],
  isBookkeeperView: boolean
): { containsMessage?: boolean; messageFrom?: string } => {
  const hasUnread = hasMessageValues.includes(true)
  const hasRead = hasMessageValues.includes(false)

  if ((hasUnread && hasRead) || (!hasUnread && !hasRead)) return {}
  if (hasUnread && !hasRead) {
    return {
      containsMessage: true,
      messageFrom: isBookkeeperView ? MESSAGE_FROM.ADMIN : MESSAGE_FROM.BOOKKEEPER,
    }
  }
  return { containsMessage: true }
}

const filterReadMessages = (
  nodes: IncidenceItem[],
  hasUnread: boolean,
  hasRead: boolean,
  isBookkeeperView: boolean
): IncidenceItem[] => {
  if (hasRead && !hasUnread) {
    return nodes.filter((incidence) => !hasUnreadMessage(incidence, isBookkeeperView))
  }
  return nodes
}

const EmployeeUpdatesDataCollectionPage = () => {
  const { navigation } = useNavigation()
  const { id } = useParams()
  const incidenceDetail = <Outlet />
  const openedIncidenceId = Number(id)
  const { hasInhousePayroll } = useInhousePayroll()
  const isBookkeeperView = useBookkeeperView()
  const workplaceFilter = useWorkplaceFilter()

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const client = useApolloClient()
  const { data } = useQuery(EmployeeUpdatesDataCollectionDocument, {
    variables: {
      companyId: companyId,
      page: DEFAULT_PAGE,
      perPage: DEFAULT_PER_PAGE,
    },
    fetchPolicy: 'network-only',
    skip: !companyId,
  })

  const { data: statusCountsData } = useQuery(EmployeeUpdatesStatusCountsDocument, {
    variables: {
      companyId: companyId,
    },
    fetchPolicy: 'cache-and-network',
    skip: !companyId,
  })

  const shouldShowEnvelopeColumn = React.useMemo(
    () =>
      (data?.bookkeepersManagement?.incidencesConnection?.nodes ?? []).some(
        (incidence: IncidenceItem) => hasUnreadMessage(incidence, isBookkeeperView)
      ),
    [data, isBookkeeperView]
  )

  const findFeatureByName = (featureName: string) => {
    return data?.features?.featuresConnection?.nodes?.find(
      (node: { name?: string }) => node?.name === featureName
    )
  }

  const isEmployeeUpdatesDataCollectionFeatureEnabled =
    findFeatureByName('dev_compensations_employee_updates_data_collection')?.enabled ?? true

  const [exportModalOpen, setExportModalOpen] = React.useState<boolean>(false)
  const [createIncidenceModalOpen, setCreateIncidenceModalOpen] = React.useState<boolean>(false)

  const defaultIncidencesFilterValues: DefaultParams = {
    filters: {
      status: hasInhousePayroll
        ? INHOUSE_INCIDENCE_STATUSES.to_do
        : INCIDENCE_STATUSES.in_preparation,
    },
    direction: 'asc',
    attribute: SORT_ATTRIBUTES.incidence_effective_date,
  }

  const defaultMessagesFilterValues: DefaultParams = {
    direction: 'desc',
    containsMessage: true,
    attribute: SORT_ATTRIBUTES.incidence_effective_date,
  }

  const handleExportClick = () => {
    track(trackingEvents.compensations.openEmployeeUpdatesExportModal, {
      application: FACTORIAL_APP,
    })
    setExportModalOpen(true)
  }

  const handleCloseCreateIncidenceModal = () => {
    setCreateIncidenceModalOpen(false)
  }

  const handleCreateIncidenceSuccess = async () => {
    setCreateIncidenceModalOpen(false)
    await client.refetchQueries({
      include: [EmployeeUpdatesDataCollectionDocument, EmployeeUpdatesStatusCountsDocument],
    })
  }

  const getStatusOptions = () => {
    if (hasInhousePayroll) {
      return Object.entries(INHOUSE_INCIDENCE_STATUSES).map(([_key, value]) => ({
        label: inhouseStatusCopies()[value],
        value,
      }))
    } else {
      const statusOrder = [
        INCIDENCE_STATUSES.in_preparation,
        INCIDENCE_STATUSES.to_do,
        INCIDENCE_STATUSES.doing,
        INCIDENCE_STATUSES.done,
        INCIDENCE_STATUSES.discarded,
      ]
      const statusCopies = professionalsStatusCopies()
      return statusOrder.map((status) => ({
        label: statusCopies[status],
        value: status,
      }))
    }
  }

  const [currentFilters, setCurrentFilters] = React.useState<FilterState>({})

  const getStatusCountCallback = React.useCallback(
    (status: string) => getStatusCount(status, statusCountsData || {}),
    [statusCountsData]
  )

  const dynamicPresets = React.useMemo(
    () => createDynamicPresets(statusCountsData || {}, getStatusCountCallback, hasInhousePayroll),
    [statusCountsData, getStatusCountCallback, hasInhousePayroll]
  )

  const hasMessageFilterRef = React.useRef<{ hasUnread: boolean; hasRead: boolean }>({
    hasUnread: false,
    hasRead: false,
  })

  const handleStateChange = React.useCallback((newState: DataCollectionState) => {
    const hasMessageValues = newState.filters?.hasMessage ?? []
    hasMessageFilterRef.current = {
      hasUnread: hasMessageValues.includes(true),
      hasRead: hasMessageValues.includes(false),
    }
    createStateChangeHandler(setCurrentFilters)(newState)
  }, [])

  const source = useGraphqlDataCollectionSource({
    document: EmployeeUpdatesDataCollectionDocument,
    mapData: (queryData: EmployeeUpdatesDataCollectionQuery) => {
      const nodes = queryData?.bookkeepersManagement?.incidencesConnection?.nodes ?? []
      const { hasUnread, hasRead } = hasMessageFilterRef.current
      return filterReadMessages(nodes, hasUnread, hasRead, isBookkeeperView)
    },
    mapVariables: ({ filters, sortings, search, pagination }) => {
      const status = filters?.status?.filter(Boolean)
      const defaultStatus =
        !hasInhousePayroll && (!status || status.length === 0)
          ? [INCIDENCE_STATUSES.in_preparation]
          : status
      const type = filters?.type?.filter(Boolean)
      const legalEntityIds = filters?.legalEntityIds?.filter(Boolean)
      const workplaceIds = Array.isArray(filters?.workplaceIds)
        ? filters.workplaceIds.map(Number).filter(Boolean)
        : undefined
      const flattenedType = type?.flat()

      const effectiveDateFrom =
        filters?.effectiveDate &&
        typeof filters.effectiveDate === 'object' &&
        'from' in filters.effectiveDate &&
        filters.effectiveDate.from instanceof Date
          ? filters.effectiveDate.from.toISOString().split('T')[0]
          : undefined

      const effectiveDateTo =
        filters?.effectiveDate &&
        typeof filters.effectiveDate === 'object' &&
        'to' in filters.effectiveDate &&
        filters.effectiveDate.to instanceof Date
          ? filters.effectiveDate.to.toISOString().split('T')[0]
          : undefined

      return {
        companyId,
        page: pagination?.currentPage ?? DEFAULT_PAGE,
        perPage: pagination?.perPage ?? DEFAULT_PER_PAGE,
        ...(defaultStatus?.length && { status: defaultStatus }),
        ...(flattenedType?.length && { type: flattenedType }),
        ...(legalEntityIds?.length && { legalEntitiesIds: legalEntityIds }),
        ...(workplaceIds?.length && { workplaceIds }),
        ...(effectiveDateFrom && { startsOn: effectiveDateFrom }),
        ...(effectiveDateTo && { endsOn: effectiveDateTo }),
        ...(search && { search }),
        ...(sortings?.[0]?.field && { sortBy: sortings[0].field }),
        ...(sortings?.[0]?.order && { direction: sortings[0].order }),
        ...getMessageFilterVariables(filters?.hasMessage ?? [], isBookkeeperView),
      }
    },
    mapPagination: (queryData: EmployeeUpdatesDataCollectionQuery) => ({
      total: queryData?.bookkeepersManagement?.incidencesConnection?.totalCount ?? 0,
    }),
    itemUrl: (item: IncidenceItem) =>
      navigation.compensations.employeeUpdatesDataCollection.id.fullPath({ id: item.id }),
    presets: dynamicPresets,
    ...(!hasInhousePayroll && {
      currentFilters: {
        status: [INCIDENCE_STATUSES.in_preparation],
      },
    }),
    filters: {
      type: {
        label: i18n.t('compensations.employee_updates_data_collection_page.filters.update_type'),
        type: 'in',
        options: {
          options: getUpdateTypeFilterOptions(),
        },
      },
      status: {
        label: i18n.t('compensations.employee_updates_data_collection_page.filters.status'),
        type: 'in',
        options: {
          options: getStatusOptions(),
        },
      },
      effectiveDate: {
        label: i18n.t('compensations.employee_updates_data_collection_page.filters.effective_date'),
        type: 'date',
        options: {
          mode: 'range',
        },
      },
      hasMessage: {
        label: i18n.t('compensations.employee_updates_data_collection_page.filters.messages'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t(
                'compensations.employee_updates_data_collection_page.filters.messages_options.read'
              ),
              value: false,
            },
            {
              label: i18n.t(
                'compensations.employee_updates_data_collection_page.filters.messages_options.unread'
              ),
              value: true,
            },
          ],
        },
      },
      legalEntityIds: {
        label: i18n.t('compensations.employee_updates_data_collection_page.filters.legal_entity'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: CompensationLegalEntitiesDocument,
              variables: {
                companyId,
              },
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.apiCore?.company?.legalEntities?.map((legalEntity) => ({
                label: legalEntity.legalName || '',
                value: Number(legalEntity.id),
              })) || []
            )
          },
        },
      },
      workplaceIds: {
        ...workplaceFilter,
        label: i18n.t('compensations.employee_updates_data_collection_page.filters.workplace'),
      },
    },
    sortings: {
      employee_first_name: {
        label: i18n.t('compensations.employee_updates_data_collection_page.columns.employee'),
      },
      incidence_type: {
        label: i18n.t('bookkeepers.incidence.table.type'),
      },
      incidence_status: {
        label: i18n.t('bookkeepers.incidence.table.status'),
      },
      legal_entity_name: {
        label: i18n.t('compensations.employee_updates_data_collection_page.filters.legal_entity'),
      },
      incidence_effective_date: {
        label: i18n.t('compensations.employee_updates_data_collection_page.filters.effective_date'),
      },
    },
    defaultSorting: {
      field: 'incidence_effective_date',
      order: 'desc',
    },
    search: {
      enabled: true,
    },
    perPage: DEFAULT_PER_PAGE, // This is temporary. We need to fix repository since totalCount isn't working as expected
    primaryActions: () => ({
      label: 'Export data',
      icon: Upload,
      onClick: handleExportClick,
    }),
    secondaryActions: {
      expanded: 1,
      actions: () => [
        {
          label: 'Create',
          icon: Plus,
          onClick: () => setCreateIncidenceModalOpen(true),
        },
      ],
    },
  })

  if (!isEmployeeUpdatesDataCollectionFeatureEnabled) {
    return <Navigate to={navigation.compensations.employeeUpdates.fullPath()} replace />
  }

  return (
    <ResponsivePage.Body>
      <IncidenceFilterProvider defaultFilterValues={defaultIncidencesFilterValues}>
        <MessageFilterProvider defaultFilterValues={defaultMessagesFilterValues}>
          <EmployeeUpdatesDataCollectionContent
            dataCollectionTable={
              <Box paddingY="s16" width="full">
                <OneDataCollection
                  key={`employee-updates-${hasInhousePayroll ? 'inhouse' : 'professionals'}`}
                  fullHeight
                  source={source}
                  onStateChange={handleStateChange}
                  storage={{
                    features: ['filters', 'sortings'],
                  }}
                  visualizations={[
                    {
                      type: 'table',
                      options: {
                        columns: [
                          {
                            label: i18n.t(
                              'compensations.employee_updates_data_collection_page.columns.employee'
                            ),
                            render: (item: IncidenceItem) => {
                              const firstName = item.employee?.firstName ?? ''
                              const lastName = item.employee?.lastName ?? ''
                              const name = [firstName, lastName].filter(Boolean).join(' ')
                              return {
                                type: 'person',
                                value: {
                                  firstName,
                                  lastName,
                                  name,
                                  src: '',
                                },
                              }
                            },
                            sorting: 'employee_first_name',
                          },
                          {
                            label: i18n.t(
                              'compensations.employee_updates_data_collection_page.columns.type'
                            ),
                            render: (item: IncidenceItem) => ({
                              type: 'text',
                              value: translatedIncidenceName(
                                item.name ?? '',
                                item.customName ?? ''
                              ),
                            }),
                            sorting: 'incidence_type',
                          },
                          {
                            label: i18n.t(
                              'compensations.employee_updates_data_collection_page.columns.status'
                            ),
                            render: (item: IncidenceItem) => {
                              const statusConfig = getStatusConfig(item.status, hasInhousePayroll)
                              return {
                                type: 'status',
                                value: {
                                  status: statusConfig.type,
                                  label: statusConfig.label,
                                },
                              }
                            },
                            sorting: 'incidence_status',
                          },
                          {
                            label: i18n.t(
                              'compensations.employee_updates_data_collection_page.columns.effective_date'
                            ),
                            render: (item: IncidenceItem) => ({
                              type: 'date',
                              value: item.startsOn ? new Date(item.startsOn) : undefined,
                            }),
                            sorting: 'incidence_effective_date',
                          },
                          // @ts-expect-error - OneDataCollection supports 'tag' type but it's not in the TypeScript definitions
                          ...(shouldShowEnvelopeColumn
                            ? [
                                {
                                  label: '',
                                  render: (item: IncidenceItem) =>
                                    hasUnreadMessage(item, isBookkeeperView)
                                      ? { type: 'tag', value: { icon: Envelope } }
                                      : '',
                                },
                              ]
                            : []),
                        ],
                        frozenColumns: 1,
                      },
                    },
                  ]}
                />
                {exportModalOpen && (
                  <PayrollExportModal
                    onClose={() => setExportModalOpen(false)}
                    selectedCompany={null}
                    isProfessionals={false}
                  />
                )}
                {createIncidenceModalOpen && (
                  <NewManualIncidenceModal
                    onClose={handleCloseCreateIncidenceModal}
                    onSuccess={handleCreateIncidenceSuccess}
                  />
                )}
              </Box>
            }
            incidenceDetail={incidenceDetail}
            openedIncidenceId={openedIncidenceId}
          />
        </MessageFilterProvider>
      </IncidenceFilterProvider>
    </ResponsivePage.Body>
  )
}

export default EmployeeUpdatesDataCollectionPage
